// frontend/src/modules/email/api.ts
import { api } from '../../core/api-client';
import type {
  EmailProviderConfig, EmailProviderConfigInput,
  EmailTemplate, EmailLogPage,
  EmailSuppression, SenderDomainState,
  EmailCampaign, EmailCampaignListItem, EmailCampaignRecipient,
  CampaignInput, SelectedRecipient,
} from './types';

export const emailApi = {
  // Provider config
  getProviderConfig: () =>
    api<{ config: EmailProviderConfig | null }>('/api/email/provider-config').then((r) => r.config),
  putProviderConfig: (input: EmailProviderConfigInput) =>
    api<{ config: EmailProviderConfig }>('/api/email/provider-config', { method: 'PUT', body: input }).then((r) => r.config),

  // Templates
  listTemplates: () =>
    api<{ templates: EmailTemplate[] }>('/api/email/templates').then((r) => r.templates),
  getTemplate: (key: string) =>
    api<{ template: EmailTemplate }>(`/api/email/templates/${encodeURIComponent(key)}`).then((r) => r.template),
  updateTemplate: (key: string, patch: { subject: string; body_html: string }) =>
    api<{ template: EmailTemplate }>(`/api/email/templates/${encodeURIComponent(key)}`,
      { method: 'PATCH', body: patch }).then((r) => r.template),
  resetTemplate: (key: string) =>
    api<{ ok: true }>(`/api/email/templates/${encodeURIComponent(key)}`, { method: 'DELETE' }),

  // Log — status/q are server-side filters (status=pending|sent|failed;
  // q = to_email substring, case-insensitive).
  listLog: (limit = 50, offset = 0, filters?: { status?: string; q?: string }) => {
    const qs = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (filters?.status) qs.set('status', filters.status);
    if (filters?.q) qs.set('q', filters.q);
    return api<EmailLogPage>(`/api/email/log?${qs.toString()}`);
  },

  // Test send
  testSend: (to: string) =>
    api<{ ok: true; log_id: string; provider_message_id: string }>('/api/email/test-send',
      { method: 'POST', body: { to } }),

  // Deliverability — sender domain
  getSenderDomain: () =>
    api<{ domain: SenderDomainState }>('/api/email/sender-domain').then((r) => r.domain),
  createSenderDomain: (domain: string) =>
    api<{ domain: SenderDomainState }>('/api/email/sender-domain',
      { method: 'POST', body: { domain } }).then((r) => r.domain),
  verifySenderDomain: () =>
    api<{ domain: SenderDomainState }>('/api/email/sender-domain/verify',
      { method: 'POST' }).then((r) => r.domain),
  deleteSenderDomain: () =>
    api<{ ok: true }>('/api/email/sender-domain', { method: 'DELETE' }),

  // Deliverability — suppressions
  listSuppressions: () =>
    api<{ suppressions: EmailSuppression[] }>('/api/email/suppressions').then((r) => r.suppressions),
  addSuppression: (email: string) =>
    api<{ suppression: EmailSuppression }>('/api/email/suppressions',
      { method: 'POST', body: { email } }).then((r) => r.suppression),
  deleteSuppression: (id: string) =>
    api<{ ok: true }>(`/api/email/suppressions/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // Campaigns
  listCampaigns: () =>
    api<{ campaigns: EmailCampaignListItem[] }>('/api/email/campaigns').then((r) => r.campaigns),
  getCampaign: (id: string) =>
    api<{ campaign: EmailCampaignListItem; recipients: EmailCampaignRecipient[] }>(
      `/api/email/campaigns/${encodeURIComponent(id)}`),
  // `emails` (selected audience only) persists the pasted recipient list with
  // the draft, so editing later preloads it and launch can reuse it.
  createCampaign: (input: CampaignInput, emails?: SelectedRecipient[] | null) =>
    api<{ campaign: EmailCampaign }>('/api/email/campaigns',
      { method: 'POST', body: { ...input, emails: emails ?? undefined } }).then((r) => r.campaign),
  updateCampaign: (id: string, patch: Partial<CampaignInput>, emails?: SelectedRecipient[] | null) =>
    api<{ campaign: EmailCampaign }>(`/api/email/campaigns/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: { ...patch, emails: emails ?? undefined } }).then((r) => r.campaign),
  deleteCampaign: (id: string) =>
    api<{ ok: true }>(`/api/email/campaigns/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  cancelCampaign: (id: string) =>
    api<{ campaign: EmailCampaign }>(`/api/email/campaigns/${encodeURIComponent(id)}/cancel`,
      { method: 'POST' }).then((r) => r.campaign),
  scheduleCampaign: (id: string, scheduledAt: string, emails?: SelectedRecipient[] | null) =>
    api<{ campaign: EmailCampaign }>(`/api/email/campaigns/${encodeURIComponent(id)}/schedule`,
      { method: 'POST', body: { scheduled_at: scheduledAt, emails: emails ?? undefined } }).then((r) => r.campaign),
  sendCampaignNow: (id: string, emails?: SelectedRecipient[] | null) =>
    api<{ campaign: EmailCampaign }>(`/api/email/campaigns/${encodeURIComponent(id)}/send-now`,
      { method: 'POST', body: { emails: emails ?? undefined } }).then((r) => r.campaign),
  campaignAudiencePreview: () =>
    api<{ all_clients: number }>('/api/email/campaigns/audience-preview'),
};
